/**
 * Config compartilhada dos 4 funis "Curso Gratuito" (LCA, Terapia Manual, SDFP, US).
 * Cada funil tem uma `source` (usada em taxonomy/tags/CRM), rota, título, cópia e aulas.
 * Copy oficial extraída das páginas RD Station (sem invenção).
 */

import pinfildiFoto from "@/assets/pinfildi.jpg.asset.json";
import fukusawaFoto from "@/assets/fukusawa.jpg.asset.json";
import marcusFoto from "@/assets/marcus.webp.asset.json";
import nayraFoto from "@/assets/nayra.jpg.asset.json";

export type FunilSlug = "funil-lca" | "funil-tmanual" | "funil-sdfp" | "funil-us";

export type FunilConfig = {
  source: FunilSlug;
  route: `/${FunilSlug}`;
  area: string;
  posNome: string;
  /** Título curto exibido no meta e hero. */
  titulo: string;
  /** Bloco de destaque no hero (2 linhas). */
  heroTitulo: [string, string];
  /** Subtítulo/apresentação do curso — copy oficial. */
  descricao: string;
  /** Chips no hero. */
  chips: string[];
  /** Lista de aulas do curso (formato ref2). */
  aulas: string[];
  /** Professor(a) responsável. */
  professor: {
    nome: string;
    bio: string;
    foto?: string;
  };
  /** Paleta de acento (primary + secondary hex). */
  cores: { primary: string; secondary: string };
  /**
   * Rota dedicada da página de oferta (thank-you page) exibida após a captura.
   * Cada funil tem a sua própria página para carimbar `utm_source` no link Eduzz.
   * LCA → /funil-lca-combo-imersoes.
   * Terapia Manual → /funil-tmanual-combo-imersoes.
   * SDFP → /funil-sdfp-combo-imersoes.
   * US → /funil-us-combo-imersoes.
   */
  offerRoute:
    | "/funil-lca-combo-imersoes"
    | "/funil-tmanual-combo-imersoes"
    | "/funil-sdfp-combo-imersoes"
    | "/funil-us-combo-imersoes";
};

export const FUNIS: Record<FunilSlug, FunilConfig> = {
  "funil-lca": {
    source: "funil-lca",
    route: "/funil-lca",
    area: "lca",
    posNome: "Curso Gratuito de Reabilitação do LCA",
    titulo: "Reabilitação do LCA",
    heroTitulo: ["Reabilitação", "do LCA"],
    descricao:
      "Aprofunde seus conhecimentos sobre avaliação, cuidados e condutas após cirurgia de reconstrução do LCA. Este é um dos mais de 120 cursos disponíveis na Star Cursos.",
    chips: ["100% gratuito", "Baseado em evidências", "Com certificado"],
    aulas: [
      "Anatomia e Biomecânica do LCA",
      "Exame Clínico e Considerações Cirúrgicas",
      "Reabilitação no Pós Operatório",
      "Retorno Esportivo na RLCA",
    ],
    professor: {
      nome: "Dr. Leandro Fukusawa",
      bio: "Leandro é fisioterapeuta com ampla experiência clínica nas áreas de ortopedia, traumatologia e esporte. É especialista em reabilitação musculoesquelética pela Faculdade de Ciências Médicas Santa Casa de São Paulo (FCMSC-SP), mestre em Fisioterapia pela Universidade Cidade de São Paulo (UNICID) e Doutor no programa de Ciências da Saúde pela FCMSC-SP.",
      foto: fukusawaFoto.url,
    },
    cores: { primary: "#dc2626", secondary: "#0d9488" },
    offerRoute: "/funil-lca-combo-imersoes",
  },
  "funil-tmanual": {
    source: "funil-tmanual",
    route: "/funil-tmanual",
    area: "tmanual",
    posNome: "Curso Gratuito de Terapia Manual Ortopédica",
    titulo: "Terapia Manual Ortopédica",
    heroTitulo: ["Terapia Manual", "Ortopédica"],
    descricao:
      "Aprenda as principais técnicas, os critérios que orientam sua utilização e as evidências por trás de cada escolha. Este é um dos mais de 120 cursos disponíveis na Star Cursos.",
    chips: ["100% gratuito", "Baseado em evidências", "Com certificado"],
    aulas: [
      "Terapia Manual Ortopédica aplicada aos MMSS",
      "Terapia Manual Ortopédica aplicada aos MMII",
      "Terapia Manual Ortopédica aplicada à Coluna Vertebral",
      "Considerações Finais",
    ],
    professor: {
      nome: "Prof. Marcus Domingues",
      bio: "Fisioterapeuta graduado pela ESEHA, formação internacional em terapia manual ortopédica, pela Manual Concepts Post Graduation Australia Institute, Body Method - UK, formação básica e avançada na abordagem multidimensional biopsicossocial (CFT), formação no sistema de classificação em subgrupos endossado pela Universidade de Pittsburgh, diretor da SPINE CURSOS e organizador das páginas Entendendo a Dor Crônica, Grupo de Estudos e Pain Arts.",
      foto: marcusFoto.url,
    },
    cores: { primary: "#7c3aed", secondary: "#f59e0b" },
    offerRoute: "/funil-tmanual-combo-imersoes",
  },
  "funil-sdfp": {
    source: "funil-sdfp",
    route: "/funil-sdfp",
    area: "sdfp",
    posNome: "Curso Gratuito de Dor Femoropatelar (SDFP)",
    titulo: "Dor Femoropatelar",
    heroTitulo: ["Abordagem na", "Dor Femoropatelar"],
    descricao:
      "Aprenda a avaliar melhor, conectar os achados clínicos e tomar decisões mais seguras em cada caso.\u00a0Este é um dos mais de 120 cursos disponíveis na Star Cursos.",
    chips: ["100% gratuito", "Baseado em evidências", "Com certificado"],
    aulas: [
      "Anatomia e Biomecânica",
      "Terminologia, Quadro Clínico e Fatores de Risco",
      "Avaliação na SDFP",
      "Tratamento na SDFP",
      "Retorno às Atividades",
    ],
    professor: {
      nome: "Profª. Nayra Rabelo",
      bio: "Fisioterapeuta, especialista em fisioterapia musculoesquelética pela Santa Casa de SP. Mestre, doutora e Pós-Doutora em Ciência da Reabilitação pela UNINOVE. Diretora de pesquisa dos grupos de Quadril e de Joelho da Santa Casa de São Paulo. Membro da Sociedade Brasileira de Análise Clínica da Marcha e do Movimento Humano (SBACMMH).",
      foto: nayraFoto.url,
    },
    cores: { primary: "#0284c7", secondary: "#059669" },
    offerRoute: "/funil-sdfp-combo-imersoes",
  },
  "funil-us": {
    source: "funil-us",
    route: "/funil-us",
    area: "us",
    posNome: "Curso Gratuito de Ultrassom Terapêutico",
    titulo: "Ultrassom Terapêutico",
    heroTitulo: ["Atualizações em", "Ultrassom"],
    descricao:
      "Descubra o que as evidências atuais mostram sobre técnicas de aplicação e indicações na área musculoesquelética.\u00a0Este é um dos mais de 120 cursos disponíveis na Star Cursos.",
    chips: ["100% gratuito", "Baseado em evidências", "Com certificado"],
    aulas: [
      "Terminologias e dosimetrias do US",
      "Aprofundando na dosimetria do US",
      "Aplicabilidade clínica: Fraturas e Osteoartrite",
      "Aplicabilidade clínica: Tendinopatias",
      "Aplicações Práticas",
    ],
    professor: {
      nome: "Dr. Carlos Eduardo Pinfildi",
      bio: "Fisioterapeuta, Mestre e Doutor em Ciências da Saúde pela UNIFESP. Pós-doutorado na Griffith University - Austrália. Atualmente é Professor Associado e orientador do programa de Pós-graduação Stricto Sensu em Ciências do Movimento Humano e Reabilitação da UNIFESP - Santos.",
      foto: pinfildiFoto.url,
    },
    cores: { primary: "#4f46e5", secondary: "#06b6d4" },
    offerRoute: "/funil-us-combo-imersoes",
  },
};
